import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

const AccordionContext = React.createContext({
  openIndex: null,
  setOpenIndex: () => {},
})

const Accordion = ({ className, children, ...props }) => {
  const [openIndex, setOpenIndex] = React.useState(null)

  return (
    <AccordionContext.Provider value={{ openIndex, setOpenIndex }}>
      <div className={cn("space-y-2", className)} {...props}>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { index })
          }
          return child
        })}
      </div>
    </AccordionContext.Provider>
  )
}
Accordion.displayName = "Accordion"

const AccordionItem = ({ className, index, children, ...props }) => {
  const { openIndex } = React.useContext(AccordionContext)
  const isOpen = openIndex === index

  return (
    <div
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm transition-all",
        isOpen && "border-primary shadow-md",
        className
      )}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { index, isOpen })
        }
        return child
      })}
    </div>
  )
}
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = ({ className, children, index, isOpen, ...props }) => {
  const { setOpenIndex } = React.useContext(AccordionContext)

  const handleClick = () => {
    setOpenIndex(isOpen ? null : index)
  }

  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center justify-between px-6 py-4 text-left font-semibold transition-all hover:text-primary [&>svg]:transition-transform",
        isOpen && "[&>svg]:rotate-180",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      <span className="flex-1 pr-4">{children}</span>
      <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
    </button>
  )
}
AccordionTrigger.displayName = "AccordionTrigger"

const AccordionContent = ({ className, children, isOpen, ...props }) => {
  return (
    <div
      className={cn(
        "overflow-hidden transition-all",
        isOpen ? "max-h-[500px] animate-accordion-down" : "max-h-0 animate-accordion-up"
      )}
      {...props}
    >
      <div className={cn("px-6 pb-4 pt-0 text-sm text-muted-foreground", className)}>
        {children}
      </div>
    </div>
  )
}
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
